import type {
  SlaRuleCreateRequestSlaActionType,
  SlaRuleDTOSlaEntityLevel,
  SlaTemplateItemListSlaTemplateSource,
} from '~/server_shared/openapi/data-contracts';

const formatCount = (count: number): string => count.toLocaleString('en-US');

/** User-facing copy for the SLA Engine screen, following the `texts.ts` convention of its sibling
 *  engines (`FundAccounts/texts.ts`). */
export const TEXTS = {
  HEADING: 'SLA Engine',
  ALERT_TITLE: 'Please Note',

  /** The design's rule-issue disclaimer. `entityCap` is the list response's `entity_cap` — the
   *  engine's `MAX_ENTITIES_PER_RULE_RUN` — so the number cannot drift from the backend constant. */
  RULE_ISSUES_DISCLAIMER: (entityCap: number): string =>
    `A run that matches more than ${formatCount(entityCap)} claims or exposures does not fire ` +
    'and puts the rule into an error state. Rule issues (a broken or deleted template, a run over the ' +
    "maximum) send a notification to the rule's creator.",

  NO_MATCHING_RULES: 'No SLA rules match the current filters.',
  NO_RULES: 'No SLA rules configured yet.',

  /** An unrestricted scope (no sub-organization / LOB selected), and the "any level" filter option. */
  ALL: 'All',
  LEVEL_LABEL: 'Level',

  COLUMN_NAME: 'Name',
  COLUMN_SUB_ORGANIZATIONS: 'Sub Organizations',
  COLUMN_LOB: 'LOB',
  COLUMN_CREATED_BY: 'Created By',
  COLUMN_ENABLE: 'Enable',
  COLUMN_ACTIONS: 'Actions',

  /** A sub-organization the org no longer lists, which therefore has no name to show. */
  UNKNOWN_SUB_ORGANIZATION: (subOrgId: number): string => `Sub-Org ${subOrgId}`,
  /** A rule with no authoring user (a configuration-repository import). */
  NO_CREATOR: '—',
  ENABLE_SWITCH_ARIA_LABEL: (ruleName: string): string => `Enable — ${ruleName}`,

  EDIT_ACTION: 'Edit',
  DELETE_ACTION: 'Delete',
  DELETE_CONFIRM_TITLE: 'Delete SLA Rule?',
  DELETE_CONFIRM_TEXT: (ruleName: string): string => `Are you sure you want to delete "${ruleName}"?`,

  ADD_RULE: 'Add Rule',
  ADD_RULE_TITLE: 'Add SLA Rule',
  EDIT_RULE_TITLE: 'Edit SLA Rule',
  SAVE: 'Save',

  RULE_NAME_LABEL: 'Rule Name',
  TEMPLATE_LABEL: 'SLA Template',
  TEMPLATE_SOURCE_LABELS: {
    system: 'System templates',
    organization: 'Organization templates',
  } satisfies Record<`${SlaTemplateItemListSlaTemplateSource}`, string>,
  /** A card that breaks the template contract; the reasons are in its tooltip. */
  TEMPLATE_UNAVAILABLE_NAME: (name: string): string => `${name} (unavailable)`,
  ENTITY_LEVEL: (levelLabel: string): string => `Level: ${levelLabel}`,
  BUSINESS_DAYS_LABEL: 'Count business days only',

  /** The shared fields take no `required` prop, so a parameter the card marks required says so in its label. */
  PARAM_LABEL: (name: string, isRequired: boolean): string => (isRequired ? `${name} *` : name),
  /** Not "All": an unset parameter takes the template's own default, which may itself narrow. */
  PARAM_UNSET_HELPER: "Not set: the template's default applies",
  PARAM_VALUE_NO_LONGER_OFFERED: (value: string): string => `${value} (no longer offered)`,
  PARAM_VALUES_TRUNCATED: 'The template offers more values than are listed here.',
  /** Slugs the rule stores that its card no longer declares, because the card was edited after the save. */
  PARAMS_NO_LONGER_DECLARED: (slugs: string[]): string =>
    `Parameters the template no longer declares: ${slugs.join(', ')}. The daily run ignores their stored ` +
    "values, and the rule can't be saved or previewed until they are removed.",
  REMOVE_UNDECLARED_PARAMS: 'Remove',

  THRESHOLDS_HEADING: 'Thresholds',
  THRESHOLD_TITLE: (position: number): string => `Threshold ${position}`,
  SLA_DAYS_LABEL: 'SLA Days',
  REPEAT_EVERY_LABEL: 'Repeat Every',
  DAYS_UNIT: (isBusinessDays: boolean): string => (isBusinessDays ? 'business days' : 'days'),
  THRESHOLD_REMOVAL_NOTE:
    "A threshold that has already fired can't be removed; changing its days creates a new threshold.",
  ADD_THRESHOLD: 'Add Threshold',
  REMOVE_THRESHOLD_ARIA_LABEL: (position: number): string => `Remove threshold ${position}`,

  ACTIONS_HEADING: 'Actions',
  ADD_ACTION: 'Add Action',
  REMOVE_ACTION_ARIA_LABEL: (position: number): string => `Remove action ${position}`,
  ACTION_TYPE_LABEL: 'Action Type',
  ACTION_TYPE_LABELS: {
    notification: 'Notification',
    email: 'Email',
    sms: 'SMS',
    email_suggestion: 'Email suggestion',
  } satisfies Record<`${SlaRuleCreateRequestSlaActionType}`, string>,
  RECIPIENT_LABEL: 'Recipient',
  RECIPIENT_NO_LONGER_AVAILABLE: (recipient: string): string => `${recipient} (no longer available)`,
  GENERIC_TEMPLATE_LABEL: 'Message Template',
  GENERIC_TEMPLATE_NO_LONGER_AVAILABLE: (templateId: number): string => `Template ${templateId} (no longer available)`,
  NOTIFICATION_TITLE_LABEL: 'Notification Title',
  NOTIFICATION_DETAILS_LABEL: 'Notification Details',

  PREVIEW_BUTTON: 'Preview',
  PREVIEW_STALE: 'The rule changed since the last preview.',
  PREVIEW_AS_OF: (snapshotDate: string): string => `As of the data snapshot of ${snapshotDate}`,
  PREVIEW_OVER_CAP: (matchedEntityCount: number, entityCap: number): string =>
    `Would match ${formatCount(matchedEntityCount)} entities, over the ${formatCount(entityCap)} cap: ` +
    'this rule would not fire and would enter an error state.',
  PREVIEW_THRESHOLD: (slaDays: number, repeatEveryDays: number, wouldFireCount: number, daysUnit: string): string =>
    `${slaDays} ${daysUnit}, repeating every ${repeatEveryDays}: would fire for ${formatCount(wouldFireCount)}`,
  PREVIEW_SAMPLE_NOTE: 'Sample of claims you can access.',
  PREVIEW_COLUMN_CLAIM: 'Claim',
  PREVIEW_COLUMN_EXPOSURE: 'Exposure',
  PREVIEW_COLUMN_ANCHOR_DATE: 'Anchor Date',
  PREVIEW_COLUMN_DAYS_ELAPSED: 'Days Elapsed',

  REQUIRED: 'Required',
  MIN_ONE_DAY: 'Must be at least 1 day',
  WHOLE_DAYS: 'Must be a whole number of days',
  DUPLICATE_SCHEDULE: 'Two thresholds cannot share the same SLA days and repeat interval.',
};

/** Keyed on the generated enum's values, so a level the backend adds is a type error here rather than
 *  a blank cell and a blank filter option — and every response's own copy of that enum indexes it. */
export const ENTITY_LEVEL_LABELS: Record<`${SlaRuleDTOSlaEntityLevel}`, string> = {
  claim: 'Claim',
  exposure: 'Exposure',
};
